import { isNil, noop } from 'lodash'
import React, { useState, type FC, useEffect } from 'react'
import { t } from 'i18next'
import { toDataURL } from 'qrcode'
import { Skeleton } from '@awell/ui-kit/components/Skeleton'
import { PathwayStatus } from 'app-care/src/generated/types-orchestration'
import { Tooltip, Button, Icon, AlertDialog } from '@awell-health/design-system'

interface HostedPagesQRButtonProps {
  hostedPagesUrlForPatient: string | undefined
  pathwayStatus: PathwayStatus
}

export const HostedPagesQRButton: FC<HostedPagesQRButtonProps> = ({ hostedPagesUrlForPatient, pathwayStatus }) => {
  const [open, setOpen] = useState(false)

  const [hasCopied, setHasCopied] = useState(false)
  const [qrCodeImageUrl, setQrCodeImageUrl] = useState<string>()

  useEffect(() => {
    if (!isNil(hostedPagesUrlForPatient)) {
      void toDataURL(hostedPagesUrlForPatient, {
        scale: 7
      }).then(url => {
        setQrCodeImageUrl(url)
      })
    }
  }, [hostedPagesUrlForPatient])

  const handleCopy = async (text: string): Promise<void> => {
    setHasCopied(true)
    await navigator.clipboard.writeText(text)
    setTimeout(() => {
      setHasCopied(false)
    }, 2000)
  }

  const qrButtonIsDisabled =
    isNil(hostedPagesUrlForPatient) ||
    pathwayStatus === PathwayStatus.Completed ||
    pathwayStatus === PathwayStatus.Stopped

  const getTooltipText = (): string => {
    if (isNil(hostedPagesUrlForPatient)) {
      return t(`qr_button_tooltip_disabled_reason_link_not_available`)
    }

    if (qrButtonIsDisabled) {
      return pathwayStatus === PathwayStatus.Completed
        ? t(`qr_button_tooltip_disabled_reason_pathway_completed`)
        : t(`qr_button_tooltip_disabled_reason_pathway_stopped`)
    }

    return t(`qr_button_tooltip_text`)
  }

  return (
    <>
      <Tooltip datatip={getTooltipText()} placement="left">
        <span>
          <Button
            variant="ghost"
            size={null}
            shape="squareLg"
            onClick={() => {
              setOpen(true)
            }}
            disabled={qrButtonIsDisabled}
          >
            <Icon icon="RiQrCodeLine" size={28} />
          </Button>
        </span>
      </Tooltip>
      {open && (
        <AlertDialog
          onClose={() => setOpen(false)}
          title={t('qr_code_for_hosted_pages')}
          text={t('qr_code_for_hosted_pages_description')}
          button={{
            label: t('close'),
            buttonAction: () => setOpen(false)
          }}
        >
          <>
            {!isNil(qrCodeImageUrl) ? (
              <img className="block mx-auto" src={qrCodeImageUrl} />
            ) : (
              <Skeleton className="block mx-auto" width={330} height={330} />
            )}
            <div className="text-center flex items-center justify-center gap-2">
              <Tooltip datatip="Click to open in new tab">
                <a
                  href={hostedPagesUrlForPatient}
                  onClick={noop}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:underline text-slate-800"
                >
                  {hostedPagesUrlForPatient}
                </a>
              </Tooltip>
              <Tooltip datatip="Copy to clipboard">
                <Button
                  variant="ghost"
                  size={null}
                  shape="squareSm"
                  onClick={() => {
                    void handleCopy(hostedPagesUrlForPatient ?? '')
                  }}
                >
                  <Icon icon={hasCopied ? 'RiCheckFill' : 'RiFileCopyFill'} size={20} />
                </Button>
              </Tooltip>
            </div>
          </>
        </AlertDialog>
      )}
    </>
  )
}
